import { useMemo } from 'react';
import { Button, Card, Col, Row, Statistic, Typography, Tag } from 'antd';
import React from 'react';
import {
  AppstoreOutlined,
  CreditCardOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useClientProducts } from '../hooks/useClientProducts';
import { useClientSubscriptions } from '../hooks/useClientSubscriptions';
import { useNavigate } from 'react-router-dom';
import { getProductStatusInfo, computeApiRequirement } from '../utils/productStatus';

const { Title, Paragraph, Text } = Typography;

const formatCurrency = (value?: number | null) => {
  if (!value) return 'R$ 0,00';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

export default function ClientDashboardPage() {
  const { user } = useAuthStore();
  const clientId = user?.id ?? null;
  const { data: rawProducts = [], isLoading: isLoadingProducts, isFetching: isFetchingProducts } = useClientProducts(clientId);
  const { data: subscriptions = [], isLoading: isLoadingSubscriptions, isFetching: isFetchingSubscriptions } = useClientSubscriptions(clientId ?? undefined);
  const navigate = useNavigate();

  // Use isFetching when clientId is available, treat as not loading when clientId is null
  // This prevents infinite skeleton state when queries are disabled (enabled: false)
  const showProductsLoading = clientId ? (isLoadingProducts || isFetchingProducts) : false;
  const showSubscriptionsLoading = clientId ? (isLoadingSubscriptions || isFetchingSubscriptions) : false;

  // Deduplicação: garantir que cada product_id apareça apenas uma vez
  const products = useMemo(() => {
    const seen = new Map();
    rawProducts.forEach((p: any) => {
      const key = p.product_id ?? p.product?.id ?? p.id;
      if (!seen.has(key)) {
        seen.set(key, p);
      } else {
        // Se já existe, manter apenas se a assinatura for mais relevante
        const existing = seen.get(key);
        const statusPriority = (status: string) => {
          switch (status) {
            case 'active': return 1;
            case 'trialing': return 2;
            case 'pending': return 3;
            case 'suspended': return 4;
            case 'cancelled': return 5;
            default: return 6;
          }
        };
        if (statusPriority(p.subscription_status) < statusPriority(existing.subscription_status)) {
          seen.set(key, p);
        }
      }
    });
    return Array.from(seen.values());
  }, [rawProducts]);

  // Calcular produtos ativos (workflows ativos)
  const activeProductsCount = useMemo(() => {
    return products.filter((p: any) => p.workflow_status === 'active').length;
  }, [products]);

  // Calcular total de assinaturas (usar a fonte principal: client_subscriptions)
  const totalSubscriptionsCount = useMemo(() => {
    return subscriptions.length;
  }, [subscriptions]);

  // Calcular gasto mensal (apenas assinaturas ativas)
  const monthlySpend = useMemo(() => {
    return subscriptions
      .filter((s: any) => s.status === 'active')
      .reduce((sum: number, s: any) => sum + Number(s.product?.monthly_price || 0), 0);
  }, [subscriptions]);

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 4, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Olá, {user?.profile?.full_name || user?.email?.split('@')[0]}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Bem-vindo ao seu painel de controle
        </Paragraph>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card 
            loading={showProductsLoading || showSubscriptionsLoading} 
            size="small"
            style={{ 
              background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.1) 0%, rgba(124, 58, 237, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(124, 58, 237, 0.2)',
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>PRODUTOS ATIVOS</Text>}
              value={activeProductsCount}
              prefix={<AppstoreOutlined style={{ color: '#7c3aed' }} />}
              valueStyle={{ color: '#7c3aed' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            loading={showProductsLoading || showSubscriptionsLoading} 
            size="small"
            style={{ 
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(236, 72, 153, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(236, 72, 153, 0.2)',
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>ASSINATURAS</Text>}
              value={totalSubscriptionsCount}
              prefix={<CreditCardOutlined style={{ color: '#ec4899' }} />}
              valueStyle={{ color: '#ec4899' }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            loading={showProductsLoading || showSubscriptionsLoading} 
            size="small"
            style={{ 
              background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(249, 115, 22, 0.05) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(249, 115, 22, 0.2)',
            }}
          >
            <Statistic
              title={<Text type="secondary" style={{ fontSize: 12 }}>GASTO MENSAL</Text>}
              value={formatCurrency(monthlySpend)}
              valueStyle={{ color: '#f97316', fontSize: 20 }}
              prefix="~"
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={[16, 16]}>
        {/* Meus Produtos */}
        <Col xs={24} lg={12}>
          <Card
            title="Meus Produtos"
            extra={
              <Button type="link" size="small" onClick={() => navigate('/client/workflows')}>
                Ver todos <RightOutlined />
              </Button>
            }
            loading={showProductsLoading}
            styles={{ body: { padding: 0 } }}
            style={{ 
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(124, 58, 237, 0.1)',
            }}
          >
            {products.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <AppstoreOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 8 }} />
                <Paragraph type="secondary">Nenhum produto contratado</Paragraph>
                <Button type="primary" onClick={() => navigate('/client/marketplace')}>
                  Explorar Marketplace
                </Button>
              </div>
            ) : (
              <div>
                {products.slice(0, 3).map((product: any) => {
                  // Extrair dados do produto
                  const subscriptionRaw = (product as any).subscription;
                  const subscription = Array.isArray(subscriptionRaw) ? subscriptionRaw[0] : subscriptionRaw;
                  const paymentStatus = subscription?.payment_status || (product as any).payment_status;
                  const trialEnd = subscription?.trial_end;
                  
                  const isActive = product.workflow_status === 'active';
                  const isFreeProduct = (product.product?.monthly_price ?? 0) <= 0;
                  
                  // Verificar formulário
                  const hasForm = Boolean(product.form_id || (product.product as any)?.form_id);
                  const hasFormTrigger = Boolean((product.product as any)?.has_form_trigger);
                  const requiresApproval = product.requires_form_approval;
                  const mustEnforceForm = requiresApproval || hasForm || hasFormTrigger;
                  const formStatus = product.form_approval_status;
                  
                  // Verificar API
                  const { requiresApi, hasApiConnected } = computeApiRequirement(product);
                  
                  // Obter status usando a função compartilhada
                  const statusInfo = getProductStatusInfo({
                    isActive,
                    paymentStatus,
                    isFreeProduct,
                    trialEnd,
                    mustEnforceForm,
                    formStatus,
                    requiresApi,
                    hasApiConnected,
                  });
                  
                  return (
                    <div
                      key={product.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background 0.2s',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            background: 
                              statusInfo.color === 'success' ? '#f6ffed' : 
                              statusInfo.color === 'error' ? '#fff2f0' : 
                              statusInfo.color === 'warning' ? '#fff7e6' : '#f5f5f5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {React.cloneElement(statusInfo.icon, {
                            style: { 
                              color: 
                                statusInfo.color === 'success' ? '#52c41a' : 
                                statusInfo.color === 'error' ? '#ff4d4f' : 
                                statusInfo.color === 'warning' ? '#fa8c16' : '#8c8c8c',
                              fontSize: 14 
                            }
                          })}
                        </div>
                        <div style={{ flex: 1 }}>
                          <Text strong style={{ display: 'block', fontSize: 13 }}>
                            {product.product?.name || 'Produto'}
                          </Text>
                          {statusInfo.isTag ? (
                            <Tag 
                              color={statusInfo.color} 
                              style={{ margin: 0, fontSize: 11 }}
                            >
                              {statusInfo.text}
                            </Tag>
                          ) : (
                            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                              {statusInfo.text}
                            </Text>
                          )}
                        </div>
                      </div>
                      <Button
                        type="default"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/client/workflows', { state: { openDetailsFor: product.id } });
                        }}
                      >
                        Detalhes
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>

        {/* Assinaturas */}
        <Col xs={24} lg={12}>
          <Card
            title="Assinaturas"
            extra={
              <Button type="link" size="small" onClick={() => navigate('/client/subscriptions')}>
                Ver todas <RightOutlined />
              </Button>
            }
            loading={showSubscriptionsLoading}
            styles={{ body: { padding: 0 } }}
            style={{ 
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(236, 72, 153, 0.1)',
            }}
          >
            {subscriptions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <CreditCardOutlined style={{ fontSize: 32, color: '#d9d9d9', marginBottom: 8 }} />
                <Paragraph type="secondary">Nenhuma assinatura ativa</Paragraph>
              </div>
            ) : (
              <div>
                {/* Mostrar todas as assinaturas */}
                {subscriptions.map((subscription: any) => {
                  const isActive = subscription.status === 'active';
                  const isOverdue = subscription.payment_status === 'overdue';
                  const isPending = subscription.status === 'pending';
                  const isTrialing = subscription.status === 'trialing';
                  
                  let displayConfig = {
                    background: '#f0f0f0',
                    iconBg: '#d9d9d9',
                    icon: <CreditCardOutlined style={{ color: '#8c8c8c', fontSize: 18 }} />,
                    textColor: '#595959',
                    statusText: subscription.status,
                    showButton: false,
                  };

                  if (isOverdue) {
                    displayConfig = {
                      background: '#fff2f0',
                      iconBg: '#ffccc7',
                      icon: <WarningOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />,
                      textColor: '#ff4d4f',
                      statusText: 'Pagamento vencido',
                      showButton: true,
                    };
                  } else if (isActive) {
                    displayConfig = {
                      background: '#f6ffed',
                      iconBg: '#d9f7be',
                      icon: <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />,
                      textColor: '#52c41a',
                      statusText: 'Ativa',
                      showButton: false,
                    };
                  } else if (isTrialing) {
                    displayConfig = {
                      background: '#e6f7ff',
                      iconBg: '#bae7ff',
                      icon: <CheckCircleOutlined style={{ color: '#1890ff', fontSize: 18 }} />,
                      textColor: '#1890ff',
                      statusText: 'Em período de teste',
                      showButton: false,
                    };
                  } else if (isPending) {
                    displayConfig = {
                      background: '#fff7e6',
                      iconBg: '#ffe7ba',
                      icon: <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 18 }} />,
                      textColor: '#fa8c16',
                      statusText: 'Aguardando confirmação',
                      showButton: false,
                    };
                  }

                  return (
                    <div
                      key={subscription.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 16px',
                        borderBottom: '1px solid #f0f0f0',
                        background: displayConfig.background,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 8,
                            background: displayConfig.iconBg,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {displayConfig.icon}
                        </div>
                        <div>
                          <Text strong style={{ color: displayConfig.textColor }}>
                            {subscription.product?.name || 'Assinatura'}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                            {displayConfig.statusText}
                            {isActive && subscription.product?.monthly_price > 0 && ` • ${formatCurrency(subscription.product.monthly_price)}/mês`}
                          </Text>
                        </div>
                      </div>
                      {displayConfig.showButton && (
                        <Button
                          type="primary"
                          danger
                          size="small"
                          onClick={() => navigate('/client/subscriptions')}
                        >
                          Pagar
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
